/**
 * SubscriptionStatus Component
 *
 * Displays current subscription status with tier info, billing date,
 * and quick actions for managing the subscription.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
  CreditCard,
  Calendar,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  RefreshCcw,
} from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import type { Subscription, SubscriptionTier } from '@/lib/subscription/types';
import { getTierConfig, isSubscriptionActive } from '@/lib/subscription/types';
import { formatPrice } from '@/lib/subscription/pricing';

// ============================================================================
// Types
// ============================================================================

interface SubscriptionStatusProps {
  subscription: Subscription | null;
  userRole: 'startup' | 'investor';
  onManageBilling?: () => void;
  onUpgrade?: () => void;
  onRetryPayment?: () => void;
  onReactivate?: () => void;
}

// ============================================================================
// Status Badge Component
// ============================================================================

function StatusBadge({ status }: { status: Subscription['status'] }) {
  const colors = useThemeColors();

  const config = {
    active: {
      icon: CheckCircle2,
      label: 'Active',
      bg: colors.traffic.green,
    },
    trialing: {
      icon: Clock,
      label: 'Trial',
      bg: colors.primary[600],
    },
    past_due: {
      icon: AlertTriangle,
      label: 'Past Due',
      bg: colors.traffic.amber,
    },
    canceled: {
      icon: XCircle,
      label: 'Canceled',
      bg: colors.traffic.red,
    },
    incomplete: {
      icon: AlertTriangle,
      label: 'Incomplete',
      bg: colors.traffic.amber,
    },
    none: {
      icon: Clock,
      label: 'Free',
      bg: colors.text.tertiary,
    },
  };

  const { icon: Icon, label, bg } = config[status];

  return (
    <View style={[styles.statusBadge, { backgroundColor: bg }]}>
      <Icon size={12} color="#fff" />
      <Text style={styles.statusText}>{label}</Text>
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SubscriptionStatus({
  subscription,
  userRole,
  onManageBilling,
  onUpgrade,
  onRetryPayment,
  onReactivate,
}: SubscriptionStatusProps) {
  const colors = useThemeColors();

  const tierConfig = subscription
    ? getTierConfig(subscription.tier, userRole)
    : null;

  const isActive = subscription ? isSubscriptionActive(subscription.status) : false;
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';
  const isTrialing = subscription?.status === 'trialing';
  const willCancel = subscription?.cancelAtPeriodEnd && !isCanceled;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /** Days remaining until the given ISO date. Returns 0 when in the past. */
  const daysUntil = (dateStr: string) => {
    const ms = new Date(dateStr).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  // No subscription - show free tier
  if (!subscription || subscription.status === 'none') {
    return (
      <Animated.View entering={FadeIn.duration(300)}>
        <View
          style={[
            styles.card,
            {
              backgroundColor: colors.surface.primary,
              borderColor: colors.border.light,
            },
          ]}
        >
          <View style={styles.header}>
            <View>
              <Text style={[styles.tierName, { color: colors.text.primary }]}>
                Free Plan
              </Text>
              <Text style={[styles.tierTagline, { color: colors.text.secondary }]}>
                Basic features included
              </Text>
            </View>
            <StatusBadge status="none" />
          </View>

          {onUpgrade && (
            <AnimatedPressable
              onPress={onUpgrade}
              style={[
                styles.upgradeButton,
                { backgroundColor: colors.primary[600] },
              ]}
            >
              <Text style={styles.upgradeButtonText}>Upgrade Now</Text>
            </AnimatedPressable>
          )}
        </View>
      </Animated.View>
    );
  }

  return (
    <Animated.View entering={FadeIn.duration(300)}>
      <View
        style={[
          styles.card,
          {
            backgroundColor: colors.surface.primary,
            borderColor: isPastDue
              ? colors.traffic.amber
              : willCancel
              ? colors.traffic.red
              : colors.border.light,
            borderWidth: isPastDue || willCancel ? 2 : 1,
          },
        ]}
      >
        {/* Past Due Warning */}
        {isPastDue && (
          <View
            style={[
              styles.warningBanner,
              { backgroundColor: `${colors.traffic.amber}15` },
            ]}
          >
            <AlertTriangle size={16} color={colors.traffic.amber} />
            <Text style={[styles.warningText, { color: colors.traffic.amber }]}>
              Payment failed. Please update your payment method.
            </Text>
          </View>
        )}

        {/* Cancellation Warning */}
        {willCancel && (
          <View
            style={[
              styles.warningBanner,
              { backgroundColor: `${colors.traffic.red}10` },
            ]}
          >
            <XCircle size={16} color={colors.traffic.red} />
            <Text style={[styles.warningText, { color: colors.traffic.red }]}>
              Cancels on {formatDate(subscription.currentPeriodEnd)}
            </Text>
          </View>
        )}

        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={[styles.tierName, { color: colors.text.primary }]}>
              {tierConfig?.name ?? subscription.tier} Plan
            </Text>
            <Text style={[styles.tierTagline, { color: colors.text.secondary }]}>
              {tierConfig?.tagline ?? 'Subscription active'}
            </Text>
          </View>
          <StatusBadge status={subscription.status} />
        </View>

        {/* Billing Info */}
        <View style={styles.infoSection}>
          <View style={styles.infoRow}>
            <View style={[styles.infoIcon, { backgroundColor: colors.primary[50] }]}>
              <CreditCard size={16} color={colors.primary[600]} />
            </View>
            <View style={styles.infoContent}>
              <Text style={[styles.infoLabel, { color: colors.text.tertiary }]}>
                Billing
              </Text>
              <Text style={[styles.infoValue, { color: colors.text.primary }]}>
                {subscription.billingInterval === 'month' ? 'Monthly' : 'Annual'}
              </Text>
            </View>
          </View>

          <View style={styles.infoRow}>
            <View style={[styles.infoIcon, { backgroundColor: colors.primary[50] }]}>
              <Calendar size={16} color={colors.primary[600]} />
            </View>
            <View style={styles.infoContent}>
              <Text style={[styles.infoLabel, { color: colors.text.tertiary }]}>
                {isTrialing ? 'Trial ends' : willCancel ? 'Ends' : 'Next billing'}
              </Text>
              <Text style={[styles.infoValue, { color: colors.text.primary }]}>
                {formatDate(subscription.currentPeriodEnd)}
                {isTrialing && (
                  <Text style={[styles.infoValue, { color: colors.warning, fontWeight: '600' }]}>
                    {`  ·  ${daysUntil(subscription.currentPeriodEnd)} day${daysUntil(subscription.currentPeriodEnd) === 1 ? '' : 's'} left`}
                  </Text>
                )}
              </Text>
            </View>
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          {isPastDue && onRetryPayment && (
            <AnimatedPressable
              onPress={onRetryPayment}
              style={[
                styles.actionButton,
                styles.primaryAction,
                { backgroundColor: colors.traffic.amber },
              ]}
            >
              <RefreshCcw size={16} color="#fff" />
              <Text style={styles.primaryActionText}>Retry Payment</Text>
            </AnimatedPressable>
          )}

          {willCancel && onReactivate && (
            <AnimatedPressable
              onPress={onReactivate}
              style={[
                styles.actionButton,
                styles.primaryAction,
                { backgroundColor: colors.primary[600] },
              ]}
            >
              <RefreshCcw size={16} color="#fff" />
              <Text style={styles.primaryActionText}>Reactivate</Text>
            </AnimatedPressable>
          )}

          {onManageBilling && (
            <AnimatedPressable
              onPress={onManageBilling}
              style={[
                styles.actionButton,
                {
                  backgroundColor: colors.surface.secondary,
                },
              ]}
            >
              <ExternalLink size={16} color={colors.text.secondary} />
              <Text style={[styles.actionText, { color: colors.text.secondary }]}>
                Manage Billing
              </Text>
            </AnimatedPressable>
          )}
        </View>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
  },
  warningText: {
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 16,
    paddingBottom: 12,
  },
  tierName: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 2,
  },
  tierTagline: {
    fontSize: 14,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
  },
  statusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  infoSection: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 12,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  infoIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoContent: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 12,
    marginBottom: 1,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    padding: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 10,
  },
  primaryAction: {},
  primaryActionText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  actionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  upgradeButton: {
    margin: 16,
    marginTop: 8,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  upgradeButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});
